import { useState, useEffect, useMemo } from "react";
import {
  Box,
  TextField,
  CircularProgress,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  Paper,
  Collapse,
  Chip,
  Stack,
  InputAdornment,
  IconButton,
  Button,
  ButtonGroup,
  TableSortLabel,
} from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from "@mui/icons-material/Search";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AppBreadcrumbs from "../../../components/AppBreadcrumbs";
import * as services from "../../../services/saleng";
import { formatNumber as formatNumberUtil } from "../../../utils/formatNumber";

const TAX_RATE = 0.07;

const STATUS_OPTIONS = [
  { label: "ส่งแล้ว", value: "sent" },
  { label: "ยืนยันแล้ว", value: "confirm" },
  { label: "รับเงินแล้ว", value: "payed" },
];

const mapPersonType = {
  normal: "บุคคลธรรมดา",
  company: "นิติบุคคล",
};

export default function SalesByCustomerSummary() {
  const formatDate = (date) => date.toISOString().split("T")[0];

  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return formatDate(d);
  });
  const [endDate, setEndDate] = useState(formatDate(new Date()));
  const [loading, setLoading] = useState(false);

  // data
  const [rows, setRows] = useState([]);
  const [openSet, setOpenSet] = useState(() => new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(STATUS_OPTIONS[0].value);

  // sort main
  const [orderBy, setOrderBy] = useState("name");
  const [order, setOrder] = useState("asc");

  // inventory avg cost
  const [invById, setInvById] = useState({});
  const [invByName, setInvByName] = useState({});

  const cellStyle = { border: "1px solid #ddd" };

  // ---------- Inventory (avg cost) ----------
  useEffect(() => {
    const loadInventory = async () => {
      try {
        const invRes = await services.getInventory();
        const invRaw = invRes?.data?.data ?? invRes?.data ?? invRes ?? [];
        const _byId = {};
        const _byName = {};
        (Array.isArray(invRaw) ? invRaw : [invRaw]).forEach((rec) => {
          const avg =
            Number(
              rec?.avgCostPerUnit ??
                rec?.averageCost ??
                rec?.avgCost ??
                rec?.costPerUnit ??
                rec?.cost ??
                0
            ) || 0;

          const pid =
            rec?.productId ??
            rec?.product?.id ??
            rec?.id ??
            rec?.product_id ??
            null;

          const pname =
            rec?.productName ??
            rec?.product?.title ??
            rec?.name ??
            rec?.product_name ??
            null;

          if (pid != null)
            _byId[String(pid)] = {
              avgCostPerUnit: avg,
              name: pname ?? undefined,
            };
          if (pname)
            _byName[String(String(pname).trim())] = {
              avgCostPerUnit: avg,
              id: pid ?? undefined,
            };
        });

        setInvById(_byId);
        setInvByName(_byName);
      } catch (e) {
        console.error("loadInventory error", e);
        setInvById({});
        setInvByName({});
      }
    };

    loadInventory();
  }, []);

  // ---- fetch sale transactions (respect status) ----
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await services.getSaleTransactions({
          ...(selectedStatus ? { status: selectedStatus } : {}),
          startDate,
          endDate,
        });
        const raw = res?.data?.data ?? res?.data ?? res ?? [];
        const grouped = groupByCustomer(raw, invById, invByName);
        setRows(grouped);
        setOpenSet(new Set());
      } catch (e) {
        console.error("fetch error", e);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [startDate, endDate, selectedStatus, invById, invByName]);

  // ---- filter ----
  const filteredRows = useMemo(() => {
    const q = (searchQuery || "").toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      const name = String(row.customer?.name ?? "").toLowerCase();
      const type = String(row.customer?.personType ?? "").toLowerCase();
      const cars = (row.carNames || []).join(" ").toLowerCase();
      return name.includes(q) || type.includes(q) || cars.includes(q);
    });
  }, [rows, searchQuery]);

  // ---- sort (main) ----
  const getRowMetrics = (row) => {
    const totalSend = sumCustom(row.items, (i) => num(i.sendWeight ?? i.netWeight));
    const totalDest = sum(row.items, "netWeight");
    const totalDeduct = totalSend - totalDest;
    const totalBefore = sum(row.items, "amountBeforeVat");
    const totalVat = sum(row.items, "vat");
    const totalNet = sum(row.items, "netPrice");
    const totalCost = sum(row.items, "costAmount");
    const totalProfit = totalBefore - totalCost;
    const unitCount = (row.items || []).length; // หน่วย = จำนวนแถวย่อย

    return {
      totalSend,
      totalDest,
      totalDeduct,
      totalBefore,
      totalVat,
      totalNet,
      totalCost,
      totalProfit,
      unitCount,
      name: row.customer?.name ?? "",
      personType: row.customer?.personType ?? "",
    };
  };

  const sortedRows = useMemo(() => {
    const data = [...filteredRows];
    data.sort((a, b) => {
      const mA = getRowMetrics(a);
      const mB = getRowMetrics(b);

      let aVal, bVal;

      switch (orderBy) {
        case "name":
          aVal = String(mA.name).toLowerCase();
          bVal = String(mB.name).toLowerCase();
          break;
        case "personType":
          aVal = String(mA.personType).toLowerCase();
          bVal = String(mB.personType).toLowerCase();
          break;
        case "totalSend":
          aVal = mA.totalSend; bVal = mB.totalSend; break;
        case "totalDeduct":
          aVal = mA.totalDeduct; bVal = mB.totalDeduct; break;
        case "totalDest":
          aVal = mA.totalDest; bVal = mB.totalDest; break;
        case "totalBefore":
          aVal = mA.totalBefore; bVal = mB.totalBefore; break;
        case "totalVat":
          aVal = mA.totalVat; bVal = mB.totalVat; break;
        case "totalNet":
          aVal = mA.totalNet; bVal = mB.totalNet; break;
        case "totalCost":
          aVal = mA.totalCost; bVal = mB.totalCost; break;
        case "totalProfit":
          aVal = mA.totalProfit; bVal = mB.totalProfit; break;
        case "unitCount":
          aVal = mA.unitCount; bVal = mB.unitCount; break;
        default:
          aVal = 0; bVal = 0;
      }

      if (typeof aVal === "string" && typeof bVal === "string") {
        const cmp = aVal.localeCompare(bVal, "th");
        return order === "asc" ? cmp : -cmp;
      } else {
        const cmp = (aVal || 0) - (bVal || 0);
        return order === "asc" ? cmp : -cmp;
      }
    });
    return data;
  }, [filteredRows, orderBy, order]);

  const requestSort = (key) => {
    if (orderBy === key) {
      setOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setOrderBy(key);
      setOrder(["name", "personType"].includes(key) ? "asc" : "desc");
    }
  };

  // ---- summary footer (ไม่ต้องพึ่ง sort) ----
  const summary = useMemo(() => {
    let totalSend = 0,
      totalDeduct = 0,
      totalDest = 0,
      totalBefore = 0,
      totalVat = 0,
      totalNet = 0,
      totalCost = 0,
      totalProfit = 0;

    filteredRows.forEach((r) => {
      (r.items || []).forEach((i) => {
        const sendW = num(i.sendWeight ?? i.netWeight);
        const destW = num(i.netWeight);
        totalSend += sendW;
        totalDest += destW;
        totalDeduct += sendW - destW;

        totalBefore += num(i.amountBeforeVat);
        totalVat += num(i.vat);
        totalNet += num(i.netPrice);
        totalCost += num(i.costAmount);
        totalProfit += num(i.amountBeforeVat) - num(i.costAmount);
      });
    });

    return {
      totalSend,
      totalDeduct,
      totalDest,
      totalBefore,
      totalVat,
      totalNet,
      totalCost,
      totalProfit,
      customerCount: filteredRows.length,
    };
  }, [filteredRows]);

  const getRowKey = (row, idx) =>
    String(row.customerId ?? row.customer?.id ?? idx);

  const toggleOpen = (key) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // ===== Exports =====
  const exportToCSV = (data, filename = "export.csv") => {
    if (!data || !data.length) return;
    const headers = Object.keys(data[0]);
    const rows = data.map((row) =>
      headers.map((field) => `"${row[field] ?? ""}"`).join(",")
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportSummary = () => {
    const dataset = sortedRows.map((row) => {
      const m = getRowMetrics(row);
      return {
        รายการ: row.customer?.name ?? "-",
        ประเภทซัพพลายเออร์: mapPersonType[row.customer?.personType] ?? "-",
        รถขนส่ง: (row.carNames || []).join(" | "),
        น้ำหนักส่งสุทธิ: fix2(m.totalSend),
        น้ำหนักหัก: fix2(m.totalDeduct),
        น้ำหนักปลายทางสุทธิ: fix2(m.totalDest),
        หน่วย: m.unitCount, // จำนวน sub rows
        ยอดก่อนรวมภาษี: fix2(m.totalBefore),
        ยอดภาษี: fix2(m.totalVat),
        ยอดสุทธิ: fix2(m.totalNet),
        ต้นทุน: fix2(m.totalCost),
        กำไร: fix2(m.totalProfit),
      };
    });
    exportToCSV(dataset, `sales_by_customer_${startDate}_to_${endDate}.csv`);
  };

  const handleExportAllItems = () => {
    const rowsFlat = [];
    sortedRows.forEach((row) => {
      (row.items || []).forEach((p) => {
        const sendW = num(p.sendWeight ?? p.netWeight);
        const destW = num(p.netWeight);
        const deductW = sendW - destW;
        const amountBefore = num(p.amountBeforeVat);
        const vat = num(p.vat);
        const total = amountBefore + vat;
        const costAmount = num(p.costAmount);
        const profit = amountBefore - costAmount;

        rowsFlat.push({
          ซัพพลายเออร์: row.customer?.name ?? "-",
          ประเภทซัพพลายเออร์: mapPersonType[row.customer?.personType] ?? "-",
          สินค้า: p.product?.title ?? p.productName ?? "-",
          น้ำหนักส่งสุทธิ: fix2(sendW),
          น้ำหนักหัก: fix2(deductW),
          น้ำหนักปลายทางสุทธิ: fix2(destW),
          หน่วย: p.unit || "กก.",
          ยอดก่อนรวมภาษี: fix2(amountBefore),
          ยอดภาษี: fix2(vat),
          ยอดสุทธิ: fix2(total),
          ต้นทุน: fix2(costAmount),
          กำไร: fix2(profit),
        });
      });
    });
    exportToCSV(rowsFlat, `sales_items_${startDate}_to_${endDate}.csv`);
  };

  // ===== UI =====
  return (
    <Box sx={{ p: 2, backgroundColor: "#f9fafb", minHeight: "100vh" }}>
      <AppBreadcrumbs />

      {/* แถวค้นหา/ช่วงเวลา */}
      <Box display="flex" justifyContent="flex-end" gap={2} my={2} flexWrap="wrap">
        <TextField
          label="ค้นหา"
          value={searchQuery}
          placeholder="ชื่อซัพพลายเออร์ / ประเภท / ทะเบียนรถ"
          onChange={(e) => setSearchQuery(e.target.value)}
          size="medium"
          sx={{ minWidth: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="เริ่มต้น"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="สิ้นสุด"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </Box>

      {/* หัวข้อ + ButtonGroup + ปุ่ม Export */}
      <Box display="flex" justifyContent="space-between" alignItems="center" my={1} gap={2} flexWrap="wrap">
        <Typography variant="h6">สรุปยอดขายสินค้ารายซัพพลายเออร์</Typography>

        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <ButtonGroup variant="outlined">
            {STATUS_OPTIONS.map((opt) => (
              <Button
                key={opt.value}
                size="large"
                onClick={() => setSelectedStatus(opt.value)}
                variant={selectedStatus === opt.value ? "contained" : "outlined"}
              >
                {opt.label}
              </Button>
            ))}
          </ButtonGroup>

          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              size="large"
              startIcon={<ContentCopyIcon />}
              onClick={handleExportSummary}
            >
              ข้อมูลหลัก
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<ContentCopyIcon />}
              onClick={handleExportAllItems}
            >
              ข้อมูลย่อยทั้งหมด
            </Button>
          </Box>
        </Box>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={5}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ backgroundColor: "#f0f0f0" }}>
              <TableRow>
                <TableCell sx={{ ...cellStyle, width: 48 }} />

                {/* รายการ (sortable) */}
                <TableCell sx={{ ...cellStyle, color: "primary.main" }} sortDirection={orderBy === "name" ? order : false}>
                  <TableSortLabel
                    active={orderBy === "name"}
                    direction={orderBy === "name" ? order : "asc"}
                    onClick={() => requestSort("name")}
                  >
                    รายการ
                  </TableSortLabel>
                </TableCell>

                {/* ประเภทซัพพลายเออร์ (sortable) */}
                <TableCell sx={{ ...cellStyle, color: "primary.main" }} sortDirection={orderBy === "personType" ? order : false}>
                  <TableSortLabel
                    active={orderBy === "personType"}
                    direction={orderBy === "personType" ? order : "asc"}
                    onClick={() => requestSort("personType")}
                  >
                    ประเภทซัพพลายเออร์
                  </TableSortLabel>
                </TableCell>

                {/* รถขนส่ง (ไม่ sortable) */}
                <TableCell sx={cellStyle}>รถขนส่ง</TableCell>

                {/* น้ำหนักส่งสุทธิ */}
                <TableCell align="right" sx={{ ...cellStyle, color: "primary.main" }} sortDirection={orderBy === "totalSend" ? order : false}>
                  <TableSortLabel
                    active={orderBy === "totalSend"}
                    direction={orderBy === "totalSend" ? order : "desc"}
                    onClick={() => requestSort("totalSend")}
                  >
                    น้ำหนักส่งสุทธิ
                  </TableSortLabel>
                </TableCell>

                {/* น้ำหนักหัก */}
                <TableCell align="right" sx={{ ...cellStyle, color: "primary.main" }} sortDirection={orderBy === "totalDeduct" ? order : false}>
                  <TableSortLabel
                    active={orderBy === "totalDeduct"}
                    direction={orderBy === "totalDeduct" ? order : "desc"}
                    onClick={() => requestSort("totalDeduct")}
                  >
                    น้ำหนักหัก
                  </TableSortLabel>
                </TableCell>

                {/* น้ำหนักปลายทางสุทธิ */}
                <TableCell align="right" sx={{ ...cellStyle, color: "primary.main" }} sortDirection={orderBy === "totalDest" ? order : false}>
                  <TableSortLabel
                    active={orderBy === "totalDest"}
                    direction={orderBy === "totalDest" ? order : "desc"}
                    onClick={() => requestSort("totalDest")}
                  >
                    น้ำหนักปลายทางสุทธิ
                  </TableSortLabel>
                </TableCell>

                {/* หน่วย = จำนวนแถวของ sub (ไม่ sortable) */}
                <TableCell sx={cellStyle}>หน่วย</TableCell>

                {/* ยอดก่อนรวมภาษี */}
                <TableCell align="right" sx={{ ...cellStyle, color: "primary.main" }} sortDirection={orderBy === "totalBefore" ? order : false}>
                  <TableSortLabel
                    active={orderBy === "totalBefore"}
                    direction={orderBy === "totalBefore" ? order : "desc"}
                    onClick={() => requestSort("totalBefore")}
                  >
                    ยอดก่อนรวมภาษี
                  </TableSortLabel>
                </TableCell>

                {/* ยอดภาษี */}
                <TableCell align="right" sx={{ ...cellStyle, color: "primary.main" }} sortDirection={orderBy === "totalVat" ? order : false}>
                  <TableSortLabel
                    active={orderBy === "totalVat"}
                    direction={orderBy === "totalVat" ? order : "desc"}
                    onClick={() => requestSort("totalVat")}
                  >
                    ยอดภาษี
                  </TableSortLabel>
                </TableCell>

                {/* ยอดสุทธิ */}
                <TableCell align="right" sx={{ ...cellStyle, color: "primary.main" }} sortDirection={orderBy === "totalNet" ? order : false}>
                  <TableSortLabel
                    active={orderBy === "totalNet"}
                    direction={orderBy === "totalNet" ? order : "desc"}
                    onClick={() => requestSort("totalNet")}
                  >
                    ยอดสุทธิ
                  </TableSortLabel>
                </TableCell>

                {/* ต้นทุน */}
                <TableCell align="right" sx={{ ...cellStyle, color: "primary.main" }} sortDirection={orderBy === "totalCost" ? order : false}>
                  <TableSortLabel
                    active={orderBy === "totalCost"}
                    direction={orderBy === "totalCost" ? order : "desc"}
                    onClick={() => requestSort("totalCost")}
                  >
                    ต้นทุน
                  </TableSortLabel>
                </TableCell>

                {/* กำไร */}
                <TableCell align="right" sx={{ ...cellStyle, color: "primary.main" }} sortDirection={orderBy === "totalProfit" ? order : false}>
                  <TableSortLabel
                    active={orderBy === "totalProfit"}
                    direction={orderBy === "totalProfit" ? order : "desc"}
                    onClick={() => requestSort("totalProfit")}
                  >
                    กำไร
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {sortedRows.map((row, idx) => {
                const key = getRowKey(row, idx);
                const isOpen = openSet.has(key);

                const m = getRowMetrics(row);

                return (
                  <>
                    <TableRow hover key={key}>
                      <TableCell sx={{ ...cellStyle, width: 48 }}>
                        <IconButton size="small" onClick={() => toggleOpen(key)} aria-label="expand row">
                          {isOpen ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                        </IconButton>
                      </TableCell>

                      <TableCell sx={cellStyle}>{row.customer?.name ?? "-"}</TableCell>
                      <TableCell sx={cellStyle}>{mapPersonType[row.customer?.personType] ?? "-"}</TableCell>
                      <TableCell sx={cellStyle}>
                        <Stack direction="row" flexWrap="wrap" gap={1}>
                          {(row.carNames || []).map((car, i) => (
                            <Chip key={i} label={car} size="small" />
                          ))}
                        </Stack>
                      </TableCell>

                      <TableCell align="right" sx={cellStyle}>{fmt(m.totalSend)}</TableCell>
                      <TableCell align="right" sx={cellStyle}>{fmt(m.totalDeduct)}</TableCell>
                      <TableCell align="right" sx={cellStyle}>{fmt(m.totalDest)}</TableCell>

                      {/* หน่วย = จำนวน sub rows */}
                      <TableCell sx={cellStyle}>{m.unitCount}</TableCell>

                      <TableCell align="right" sx={cellStyle}>{fmt(m.totalBefore)}</TableCell>
                      <TableCell align="right" sx={cellStyle}>{fmt(m.totalVat)}</TableCell>
                      <TableCell align="right" sx={cellStyle}>{fmt(m.totalNet)}</TableCell>
                      <TableCell align="right" sx={cellStyle}>{fmt(m.totalCost)}</TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          ...cellStyle,
                          color: m.totalProfit > 0 ? "green" : m.totalProfit < 0 ? "red" : "inherit",
                          fontWeight: "bold",
                        }}
                      >
                        {fmt(m.totalProfit)}
                      </TableCell>
                    </TableRow>

                    {/* Sub list */}
                    <TableRow>
                      <TableCell style={{ padding: 0 }} colSpan={13}>
                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                          <Box p={2}>
                            <Typography variant="subtitle2" mb={1}>รายการสินค้า</Typography>
                            <Table>
                              <TableHead sx={{ backgroundColor: "#f0f0f0" }}>
                                <TableRow>
                                  <TableCell sx={cellStyle}>รายการ</TableCell>
                                  <TableCell align="right" sx={cellStyle}>น้ำหนักส่งสุทธิ</TableCell>
                                  <TableCell align="right" sx={cellStyle}>น้ำหนักหัก</TableCell>
                                  <TableCell align="right" sx={cellStyle}>น้ำหนักปลายทางสุทธิ</TableCell>
                                  <TableCell sx={cellStyle}>หน่วย</TableCell>
                                  <TableCell align="right" sx={cellStyle}>ยอดก่อนรวมภาษี</TableCell>
                                  <TableCell align="right" sx={cellStyle}>ยอดภาษี</TableCell>
                                  <TableCell align="right" sx={cellStyle}>ยอดสุทธิ</TableCell>
                                  <TableCell align="right" sx={cellStyle}>ต้นทุน</TableCell>
                                  <TableCell align="right" sx={cellStyle}>กำไร</TableCell>
                                </TableRow>
                              </TableHead>

                              <TableBody>
                                {(row.items || []).map((p, i) => {
                                  const sendW = Number(p.sendWeight ?? p.netWeight ?? 0);
                                  const destW = Number(p.netWeight ?? 0);
                                  const deductW = sendW - destW;

                                  const amountBefore = Number(
                                    p.amountBeforeVat ?? destW * Number(p.pricePerUnit ?? 0)
                                  );
                                  const vat = Number(p.vat ?? amountBefore * TAX_RATE);
                                  const total = amountBefore + vat;

                                  const costAmount = Number(p.costAmount ?? 0);
                                  const profit = amountBefore - costAmount;

                                  return (
                                    <TableRow key={i}>
                                      <TableCell sx={cellStyle}>
                                        {p.product?.title ?? p.productName ?? "-"}
                                      </TableCell>
                                      <TableCell align="right" sx={cellStyle}>
                                        {formatNumber(sendW)}
                                      </TableCell>
                                      <TableCell align="right" sx={cellStyle}>
                                        {formatNumber(deductW)}
                                      </TableCell>
                                      <TableCell align="right" sx={cellStyle}>
                                        {formatNumber(destW)}
                                      </TableCell>
                                      <TableCell sx={cellStyle}>
                                        {p.unit || "กก."}
                                      </TableCell>
                                      <TableCell align="right" sx={cellStyle}>
                                        {formatNumber(amountBefore)}
                                      </TableCell>
                                      <TableCell align="right" sx={cellStyle}>
                                        {formatNumber(vat)}
                                      </TableCell>
                                      <TableCell align="right" sx={cellStyle}>
                                        {formatNumber(total)}
                                      </TableCell>
                                      <TableCell align="right" sx={cellStyle}>
                                        {formatNumber(costAmount)}
                                      </TableCell>
                                      <TableCell
                                        align="right"
                                        sx={{
                                          ...cellStyle,
                                          color: profit > 0 ? "green" : profit < 0 ? "red" : "inherit",
                                          fontWeight: "bold",
                                        }}
                                      >
                                        {formatNumber(profit)}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </>
                );
              })}
            </TableBody>

            <TableFooter>
              <TableRow
                sx={{
                  backgroundColor: "#fafafa",
                  "& td": { fontSize: "14px", fontWeight: "bold" },
                }}
              >
                <TableCell sx={cellStyle} />
                <TableCell sx={cellStyle} colSpan={3}>
                  รวมซัพพลายเออร์ {summary.customerCount.toLocaleString("th-TH")} ราย
                </TableCell>
                <TableCell align="right" sx={cellStyle}>
                  {fmt(summary.totalSend)}
                </TableCell>
                <TableCell align="right" sx={cellStyle}>
                  {fmt(summary.totalDeduct)}
                </TableCell>
                <TableCell align="right" sx={cellStyle}>
                  {fmt(summary.totalDest)}
                </TableCell>
                {/* หน่วยรวมทั้งหน้า = จำนวน sub ทั้งหมด */}
                <TableCell sx={cellStyle}>
                  {filteredRows.reduce((acc, r) => acc + (r.items?.length || 0), 0)}
                </TableCell>
                <TableCell align="right" sx={cellStyle}>
                  {fmt(summary.totalBefore)}
                </TableCell>
                <TableCell align="right" sx={cellStyle}>
                  {fmt(summary.totalVat)}
                </TableCell>
                <TableCell align="right" sx={cellStyle}>
                  {fmt(summary.totalNet)}
                </TableCell>
                <TableCell align="right" sx={cellStyle}>
                  {fmt(summary.totalCost)}
                </TableCell>
                <TableCell align="right" sx={cellStyle}>
                  {fmt(summary.totalProfit)}
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

/* ---------------- Helpers ---------------- */

function groupByCustomer(data = [], invById = {}, invByName = {}) {
  const grouped = {};

  for (const row of data) {
    const cid = row.customerId ?? row.customer?.id ?? "__unknown__";
    if (!grouped[cid]) {
      grouped[cid] = {
        customerId: cid,
        customer: row.customer ?? { id: cid, name: "-" },
        carNames: [],
        items: [],
      };
    }

    const carName = row.car?.license
      ? `${row.car?.type || ""} ${row.car.license}`.trim()
      : "ไม่มีข้อมูล";
    if (carName && !grouped[cid].carNames.includes(carName)) {
      grouped[cid].carNames.push(carName);
    }

    const isVat = row.isVat === true;

    const items = (row.items ?? []).map((it) => {
      const destW = num(it.netWeight ?? it.weight);
      const sendW = num(it.sendWeight ?? destW);

      const pricePerUnit = num(it.pricePerUnit);
      const amountBefore = num(it.amountBeforeVat ?? destW * pricePerUnit);
      const vat = isVat ? num(it.vat ?? amountBefore * TAX_RATE) : 0;
      const netPrice = num(it.netPrice ?? amountBefore + vat);

      // ---- cost lookup from inventory ----
      const pid = it.productId ?? it.product?.id ?? null;
      const pname = String(it.productName ?? it.product?.title ?? "-").trim();

      const invCostById =
        pid != null ? invById[String(pid)]?.avgCostPerUnit : undefined;
      const invCostByName = pname ? invByName[pname]?.avgCostPerUnit : undefined;

      const costPerUnit = num(it.costPerUnit ?? invCostById ?? invCostByName ?? 0);
      const costAmount = num(it.costAmount ?? costPerUnit * destW);

      return {
        ...it,
        productName: pname,
        unit: it.unit || "กก.",
        sendWeight: sendW,
        netWeight: destW,
        pricePerUnit,
        amountBeforeVat: amountBefore,
        vat,
        netPrice,
        costPerUnit,
        costAmount,
      };
    });

    grouped[cid].items.push(...items);
  }

  // รวมสินค้าซ้ำ (ต่อ 1 ซัพพลายเออร์) ตามชื่อสินค้า
  return Object.values(grouped).map((entry) => {
    const merged = {};
    for (const it of entry.items || []) {
      const key = it.product?.title ?? it.productName ?? "-";
      if (!merged[key]) {
        merged[key] = { ...it };
      } else {
        merged[key].sendWeight += num(it.sendWeight);
        merged[key].netWeight += num(it.netWeight);
        merged[key].amountBeforeVat += num(it.amountBeforeVat);
        merged[key].vat += num(it.vat);
        merged[key].netPrice += num(it.netPrice);
        merged[key].costAmount += num(it.costAmount);
      }
    }
    return { ...entry, items: Object.values(merged) };
  });
}

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}
function fmt(v) {
  return num(v).toLocaleString("th-TH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
// ใช้ formatNumber util ที่มีอยู่ในโปรเจกต์
function formatNumber(v) {
  return typeof formatNumberUtil === "function"
    ? formatNumberUtil(v)
    : fmt(v);
}
function sum(arr, key) {
  return (arr || []).reduce((a, x) => a + num(x?.[key]), 0);
}
function sumCustom(arr, fn) {
  return (arr || []).reduce((a, x) => a + num(fn(x)), 0);
}
function fix2(v) {
  return Number(num(v)).toFixed(2);
}
